// src/components/ui/forms/ComponentForm/ComponentForm.jsx
import React, { useState, useEffect } from 'react';
import {
  X,
  Save,
  Package,
  DollarSign,
  Clock,
  Type,
  FileText
} from 'lucide-react';
import { cn } from '../../../../utils/cn';

const ComponentForm = ({
  component = null, // null for create, component object for edit
  onSave,
  onCancel,
  saving = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration_minutes: '',
    component_type: 'addon'
  });

  const [validationErrors, setValidationErrors] = useState({});

  const isEditing = !!component;

  // Initialize form data
  useEffect(() => {
    if (component) {
      setFormData({
        name: component.name || '',
        description: component.description || '',
        price: component.price || '',
        duration_minutes: component.duration_minutes || '',
        component_type: component.component_type || 'addon'
      });
    }
  }, [component]);

  // Handle field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.name?.trim()) {
      errors.name = 'Add-on name is required';
    }

    if (formData.price && (isNaN(formData.price) || parseFloat(formData.price) < 0)) {
      errors.price = 'Price must be a valid positive number';
    }

    if (formData.duration_minutes && (isNaN(formData.duration_minutes) || parseInt(formData.duration_minutes) < 0)) {
      errors.duration_minutes = 'Duration must be 0 or more minutes';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const submitData = {
      name: formData.name.trim(),
      description: formData.description?.trim() || null,
      price: formData.price ? parseFloat(formData.price) : null,
      duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : 0,
      component_type: 'addon'
    };

    onSave(submitData);
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-slate-800/30 backdrop-blur-xl border border-slate-700/50 rounded-2xl w-full max-w-md shadow-2xl">
        {/* Glassmorphism overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none rounded-2xl" />
        
        {/* Header */}
        <div className="relative p-6 border-b border-slate-700/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Package className="w-5 h-5 text-purple-400" />
              </div>
              <h2 className="text-xl font-semibold text-slate-200">
                {isEditing ? 'Edit Add-on' : 'Add Optional Add-on'}
              </h2>
            </div>
            
            <button
              onClick={onCancel}
              className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors text-slate-400 hover:text-slate-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="relative p-6 space-y-4">
          {/* Add-on Name */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-300">
              <Type className="w-4 h-4" />
              Add-on Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder="Enter add-on name..."
              className={cn(
                "w-full px-4 py-3 rounded-xl transition-all duration-200",
                "text-slate-200 placeholder-slate-400",
                "bg-slate-700/50 border border-slate-600/50",
                "focus:outline-none focus:border-slate-500/50 focus:ring-2 focus:ring-slate-500/20 focus:bg-slate-700/70",
                "hover:border-slate-500/50",
                validationErrors.name && "border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20"
              )}
              autoFocus
            />
            {validationErrors.name && (
              <p className="text-red-400 text-sm flex items-center gap-1">
                <span className="w-4 h-4 rounded-full bg-red-500/20 flex items-center justify-center">!</span>
                {validationErrors.name}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-300">
              <FileText className="w-4 h-4" />
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Describe what this add-on includes..."
              rows={3}
              className="w-full px-4 py-3 rounded-xl transition-all duration-200 resize-none text-slate-200 placeholder-slate-400 bg-slate-700/50 border border-slate-600/50 focus:outline-none focus:border-slate-500/50 focus:ring-2 focus:ring-slate-500/20 focus:bg-slate-700/70 hover:border-slate-500/50"
            />
          </div>

          {/* Price and Duration Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Price */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-300">
                <DollarSign className="w-4 h-4" />
                Price
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => handleFieldChange('price', e.target.value)}
                placeholder="0.00"
                className={cn(
                  "w-full px-4 py-3 rounded-xl transition-all duration-200",
                  "text-slate-200 placeholder-slate-400",
                  "bg-slate-700/50 border border-slate-600/50",
                  "focus:outline-none focus:border-slate-500/50 focus:ring-2 focus:ring-slate-500/20 focus:bg-slate-700/70",
                  "hover:border-slate-500/50",
                  validationErrors.price && "border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20"
                )}
              />
              {validationErrors.price && (
                <p className="text-red-400 text-sm">{validationErrors.price}</p>
              )}
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-300">
                <Clock className="w-4 h-4" />
                Duration (minutes)
              </label>
              <input
                type="number"
                min="0"
                value={formData.duration_minutes}
                onChange={(e) => handleFieldChange('duration_minutes', e.target.value)}
                placeholder="0"
                className={cn(
                  "w-full px-4 py-3 rounded-xl transition-all duration-200",
                  "text-slate-200 placeholder-slate-400",
                  "bg-slate-700/50 border border-slate-600/50",
                  "focus:outline-none focus:border-slate-500/50 focus:ring-2 focus:ring-slate-500/20 focus:bg-slate-700/70",
                  "hover:border-slate-500/50",
                  validationErrors.duration_minutes && "border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20"
                )}
              />
              {validationErrors.duration_minutes && (
                <p className="text-red-400 text-sm">{validationErrors.duration_minutes}</p>
              )}
            </div>
          </div>

          {/* Helper Text */}
          <div className="bg-slate-700/30 rounded-lg p-3 text-sm">
            <div className="text-slate-300 font-medium mb-2">Optional Add-ons:</div>
            <div className="space-y-1 text-slate-400">
              <div className="flex items-start gap-2">
                <span className="text-slate-300">•</span>
                <span>Customers can choose to add these extras to their service</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-slate-300">•</span>
                <span>Price and duration are added to the base service total</span>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="relative p-6 border-t border-slate-700/50">
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-6 py-3 text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={saving || !formData.name?.trim()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-medium hover:from-purple-500 hover:to-blue-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/25"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? 'Update Add-on' : 'Create Add-on'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentForm;